import React, { useState, useEffect, useMemo } from "react";
import { makeStyles, createStyles, Theme } from "@material-ui/core";
import { useLocation } from "react-router-dom";
import { useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import {
  BUSINESS_RULES_NAVIGATION_TABS,
  CATALOG_NAVIGATION_TABS,
  PERFORMANCE_NAVIGATION_TABS,
  REPORT_TYPE_NAVIGATION_TABS,
  SEQUENCE_MODE_TYPE,
} from "../utils/Constants";
import { updateProductListSequenceMode } from "../store/product-list/ProductListActions";
import { selectProductSequenceMode, selectSavingCategoryIds } from "../store/product-list/ProductListSelectors";
import ConfirmationActionSequence from "./ConfirmationActionSequence";
import {
  selectAccountFeatures,
  selectCallerAccountId,
  selectIsKPIExecutiveSummaryFeatureEnabled,
} from "../store/app-config/AppConfigSelectors";
import TabsComponent from "./common/Tabs";
import AppState from "store/AppState";
import { filteredTabs } from "utils/TabsUtil";
import { useHasEntitlement } from "../hooks/useEntitlement";

const useStyle = makeStyles((theme: Theme) =>
  createStyles({
    indicator: {
      display: "flex",
      justifyContent: "center",
      backgroundColor: "transparent",
      "& > span": {
        maxWidth: "75%",
        width: "100%",
        backgroundColor: "rgb(112, 155, 177)",
      },
    },
    tab: {
      minWidth: "100px",
      whiteSpace: "nowrap",
    },
  }),
);

type BaseRoute = {
  name: { id: string; defaultMessage: string };
  link: string;
  activeIndex: number;
};

const TabsMenu = () => {
  const location = useLocation();
  const classes = useStyle();
  const dispatch = useDispatch();
  const userAccountId = useSelector(selectCallerAccountId);
  const isKPIExecutiveSummaryFeatureEnabled = useSelector(
    selectIsKPIExecutiveSummaryFeatureEnabled,
  );

  const showUnimplemented =
    window.localStorage.getItem("showUnimplemented") === "true";

  const features = useSelector((state: AppState) =>
    selectAccountFeatures(state, userAccountId),
  );
  const isDashboardEnabled = features?.["performance-dashboard"] ?? false;

  const hasCatalogEntitlement = useHasEntitlement("catalog");
  const hasAnalyticsEntitlement = useHasEntitlement("analytics");

  // Create routes array with useMemo to prevent recreation on every render
  const routes = useMemo(() => {
    const baseRoutes: BaseRoute[] = [];

    if (hasCatalogEntitlement) {
      baseRoutes.push({
        name: { id: "tabsMenu.catalog", defaultMessage: "Catalog" },
        link: `/catalog/${CATALOG_NAVIGATION_TABS.PRODUCTS}`,
        activeIndex: 0,
      });
    }

    if (hasCatalogEntitlement) {
      baseRoutes.push({
        name: { id: "tabsMenu.sortingRules", defaultMessage: "Sorting Rules" },
        link: `/business-rules/${BUSINESS_RULES_NAVIGATION_TABS.SORTING_RULES}`,
        activeIndex: 1,
      });
    }

    if (hasCatalogEntitlement) {
      baseRoutes.push({
        name: {
          id: "businessRulesTabs.schedules",
          defaultMessage: "Schedules",
        },
        link: `/business-rules/${BUSINESS_RULES_NAVIGATION_TABS.AUTOMATION_RULES}`,
        activeIndex: 1,
      });
    }

    if (hasAnalyticsEntitlement) {
      baseRoutes.push({
        name: { id: "tabsMenu.product", defaultMessage: "Product" },
        link: `/performance/${PERFORMANCE_NAVIGATION_TABS.PRODUCT}`,
        activeIndex: 2,
      });
      baseRoutes.push({
        name: { id: "tabsMenu.conversion", defaultMessage: "Conversion" },
        link: `/performance/${PERFORMANCE_NAVIGATION_TABS.CONVERSION}`,
        activeIndex: 2,
      });
      baseRoutes.push({
        name: { id: "tabsMenu.revenue", defaultMessage: "Revenue" },
        link: `/performance/${PERFORMANCE_NAVIGATION_TABS.REVENUE}`,
        activeIndex: 2,
      });
    }

    if (showUnimplemented) {
      baseRoutes.push({
        name: { id: "tabsMenu.reports", defaultMessage: "Reports" },
        link: `/reports/${REPORT_TYPE_NAVIGATION_TABS.PRODUCT}`,
        activeIndex: 1,
      });
    }

    if (
      hasAnalyticsEntitlement &&
      isDashboardEnabled &&
      isKPIExecutiveSummaryFeatureEnabled
    ) {
      baseRoutes.push({
        name: { id: "tabsMenu.performance", defaultMessage: "Dashboard" },
        link: `/performance/${PERFORMANCE_NAVIGATION_TABS.TRAFFIC}`,
        activeIndex: 2,
      });
    } else if (hasAnalyticsEntitlement && isDashboardEnabled) {
      baseRoutes.push({
        name: { id: "tabsMenu.performance", defaultMessage: "Dashboard" },
        link: `/performance/${PERFORMANCE_NAVIGATION_TABS.TRAFFIC}`,
        activeIndex: 2,
      });
    }

    return baseRoutes;
  }, [
    showUnimplemented,
    isDashboardEnabled,
    isKPIExecutiveSummaryFeatureEnabled,
    hasCatalogEntitlement,
    hasAnalyticsEntitlement,
  ]);

  const filteredRoutes = filteredTabs(routes);
  const currentTab =
    routes.find((route) => {
      const routeBase = route.link.split("/").slice(0, 2).join("/");
      return location.pathname.startsWith(routeBase);
    })?.activeIndex ?? 0;
  const [value, setValue] = useState(currentTab);
  const history = useHistory();
  const isSequenceUpdated = useSelector(selectProductSequenceMode);
  const savingCategoryIds = useSelector(selectSavingCategoryIds);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedTabIndex, setSelectedTabIndex] = useState<number | null>(null);

  const handleConfirmation = (checked?, tabIndex?: number) => {
    // Don't show warning if a save is in progress - allow navigation directly
    const isSaving = savingCategoryIds.length > 0;
    if (checked && isSequenceUpdated && !isSaving) {
      setSelectedTabIndex(tabIndex || null);
      setIsOpen(true);
    } else {
      const finalTabIndex =
        selectedTabIndex !== null
          ? selectedTabIndex
          : tabIndex !== undefined
            ? tabIndex
            : value;
      // Find the route by array index
      const targetRoute = filteredRoutes[finalTabIndex];
      if (targetRoute) {
        const link = targetRoute.link + `?accountId=${userAccountId}`;
        setValue(finalTabIndex);
        setIsOpen(false);
        history.push(link);
        dispatch(updateProductListSequenceMode(SEQUENCE_MODE_TYPE.VIEW));
      }
      setSelectedTabIndex(null); // Reset after navigation
    }
  };

  const handleChange = (event: React.ChangeEvent<{}>, newValue: number) => {
    if (!isSequenceUpdated && value !== newValue) {
      // close all websocket subscriptions
      //dispatch({ type: CLOSE_ALL_SUBSCRIPTIONS });
      //dispatch(resetCategory());
      //dispatch(resetProductList());
      //dispatch(resetProducts());
      setValue(newValue);
    }
  };

  useEffect(() => {
    if (location.pathname === "/catalog/products" && location.search !== "") {
      localStorage.setItem("catalogPath", location.search);
    }
  }, [location]);

  useEffect(() => {
    const link = location.pathname.split("/")[1];
    filteredRoutes.forEach((route) => {
      if (route.name.defaultMessage === "Catalog") {
        const categoryPath = localStorage.getItem("catalogPath");
        if (
          location.pathname ===
            `/catalog/${CATALOG_NAVIGATION_TABS.PRODUCTS}` &&
          location.search === ""
        ) {
          localStorage.setItem("catalogPath", "");
          route.link = `/catalog/${CATALOG_NAVIGATION_TABS.PRODUCTS}`;
        } else {
          route.link = `/catalog/${CATALOG_NAVIGATION_TABS.PRODUCTS}${categoryPath !== null ? categoryPath : ""}`;
        }
      }
      if (`/${link}` === route.link) {
        setValue(route.activeIndex);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location, filteredRoutes]);

  return (
    <TabsComponent
      tabsId="TabsMenu"
      classes={classes}
      value={value}
      routes={filteredRoutes}
      userAccountId={userAccountId}
      handleChange={handleChange}
      handleConfirmation={handleConfirmation}
      isSequenceUpdated={isSequenceUpdated}
    >
      <ConfirmationActionSequence
        isModalOpen={isOpen}
        setIsModalOpen={setIsOpen}
        handleChange={() => handleConfirmation()}
      />
    </TabsComponent>
  );
};

export default TabsMenu;
